import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
  Heading,
  Label,
  ScheduledStatus,
  shouldUpdateConsultingScheduledStatus,
  Text,
} from '@prenuvo/shared-library';
import {
  formatUnixDatetime,
  getMomentFromUnixTimestamp,
  titleCase,
} from 'packages/formatters';
import { fetchAppsResource } from 'packages/apis';
import styled from 'styled-components';
import { buildStudySelector } from '../../store/studies/selectors';
import { updateConsultingPractitioner } from '../../store/studies/actions';
import { Row, Column } from '../Layout';
import { selectStaff } from '../../store/staff/selectors';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 10px 0px;
`;

const ConsultContainer = styled.div`
  padding: 11px;
`;

const ScheduleConsultInFrontDesk = ({
  shouldDisplayScheduleConsult,
}: {
  shouldDisplayScheduleConsult: boolean;
}) => {
  const dispatch = useDispatch();
  const { studyId } = useParams();
  const selectStudy = buildStudySelector(studyId);
  const study = useSelector(selectStudy);

  const staff = useSelector(selectStaff);
  const findInStaff = (userId: string) => staff.data.find((member) => member.userId === userId);
  const consultTime = study?.practitioners?.consulting?.consultTime || null;
  const calendlyStartTimeMoment = consultTime
    ? getMomentFromUnixTimestamp(consultTime).tz(study?.location?.timezone)
    : null;
  const calendlyStartTimezone = calendlyStartTimeMoment ? calendlyStartTimeMoment.zoneAbbr() : null;

  const patientConsultDoneBy = findInStaff(study.practitioners?.consulting?.prenuvoID);

  const syncCalendlyEventStatus = async () => {
    const { parsedBody } = await fetchAppsResource<{ startTime: string; status: string }>(
      `/calendly/${study?.practitioners?.consulting?.eventID}/time`,
    );
    const { startTime, status } = parsedBody;

    if (status) {
      const updatedConsultingStatus = shouldUpdateConsultingScheduledStatus(
        status,
        startTime,
        study?.practitioners?.consulting?.scheduledStatus,
      );

      if (updatedConsultingStatus) {
        await updateConsultingPractitioner(studyId, {
          ...study.practitioners.consulting,
          scheduledStatus: updatedConsultingStatus,
        })(dispatch);
      }
    }
  };

  useEffect(() => {
    if (study?.practitioners?.consulting?.eventID) {
      syncCalendlyEventStatus();
    }
  }, [study?.practitioners?.consulting?.eventID]);

  return (
    <Container>
      <ConsultContainer>
        <Heading size={7}>NP Consult</Heading>
        {shouldDisplayScheduleConsult && (
          <Row>
            <Column>
              <Label>Consult booked</Label>
              <Text>
                {titleCase(study.practitioners?.consulting?.scheduledStatus) || 'Unknown'}
              </Text>
            </Column>
            <Column>
              <Label>Consult booked at</Label>
              <Text>
                {study.practitioners?.consulting?.scheduledAt
                  ? formatUnixDatetime(
                      study.practitioners?.consulting?.scheduledAt,
                      study.location.timezone,
                    )
                  : 'N/A'}
              </Text>
            </Column>
            <Column>
              <Label>Consult booked for</Label>
              <Text>
                {calendlyStartTimeMoment
                  ? `${calendlyStartTimeMoment.format(
                      'DD MMM YYYY',
                    )} at ${calendlyStartTimeMoment.format('hh:mma')} ${calendlyStartTimezone}`
                  : 'N/A'}
              </Text>
            </Column>
          </Row>
        )}
        {study.practitioners?.consulting?.scheduledStatus === ScheduledStatus.COMPLETE && (
          <Row>
            <Text>
              The patient had a consult with {patientConsultDoneBy?.firstname}{' '}
              {patientConsultDoneBy?.lastname} at{' '}
              {getMomentFromUnixTimestamp(
                study.practitioners?.consulting?.consultTime,
                study.location.timezone,
              ).format('DD MMM YYYY')}{' '}
              at{' '}
              {getMomentFromUnixTimestamp(
                study.practitioners?.consulting?.consultTime,
                study.location.timezone,
              ).format('hh:mma')}
              .
            </Text>
          </Row>
        )}
      </ConsultContainer>
    </Container>
  );
};

export default ScheduleConsultInFrontDesk;
